// src/pages/Partners/Partners.jsx
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Close,
  School,
  Business,
  People,
  Handshake,
  ArrowForward,
  CheckCircle,
  LocationOn,
  Email,
  Phone,
  Person,
  Send,
  Diversity1,
  Lightbulb,
  TrendingUp,
  WorkspacePremium,
} from "@mui/icons-material";
import styles from "./Partners.module.css";

// Partner Stats
const statsData = [
  { number: "5+", label: "University Partners", icon: <School /> },
  { number: "10+", label: "Active Collaborations", icon: <Handshake /> },
  { number: "50+", label: "Student Interns", icon: <People /> },
  { number: "3", label: "Countries", icon: <LocationOn /> },
];

// Partner Types
const partnerTypes = [
  {
    id: "schools",
    title: "Schools",
    icon: <School sx={{ fontSize: 40 }} />,
    description:
      "Technology-oriented product development and implementation in schools.",
    color: "#8cc61d",
    features: [
      "Technology Implementation",
      "Product Development",
      "Educational Solutions",
    ],
  },
  {
    id: "universities",
    title: "Universities",
    icon: <Business sx={{ fontSize: 40 }} />,
    description:
      "Research collaboration, internship programs, and technology innovation partnerships.",
    color: "#4a90d9",
    features: [
      "Research Collaboration",
      "Internship Programs",
      "Technology Innovation",
    ],
  },
  {
    id: "corporate",
    title: "Corporate Partners",
    icon: <Diversity1 sx={{ fontSize: 40 }} />,
    description:
      "Strategic partnerships with technology leaders for integrated solutions.",
    color: "#9b59b6",
    features: [
      "Strategic Alliances",
      "Joint Ventures",
      "Technology Integration",
    ],
  },
];

// Partner Benefits
const benefits = [
  {
    icon: <Lightbulb />,
    title: "Innovation Access",
    description: "Access to cutting-edge technology and innovative solutions",
  },
  {
    icon: <TrendingUp />,
    title: "Growth Opportunities",
    description: "Expand your institution's technological capabilities",
  },
  {
    icon: <WorkspacePremium />,
    title: "Expert Training",
    description: "Training and development programs for students and staff",
  },
  {
    icon: <Handshake />,
    title: "Long-term Collaboration",
    description: "Sustainable partnerships for mutual growth and success",
  },
];

// University Partners (Nigeria focus)
const universityPartners = [
  {
    name: "University of Lagos",
    location: "Lagos, Nigeria",
    programs: [
      "Internship Program",
      "Research Collaboration",
      "Tech Workshops",
    ],
    status: "Active",
  },
  {
    name: "Covenant University",
    location: "Ota, Nigeria",
    programs: ["Student Internships", "Joint Research", "Technology Transfer"],
    status: "Active",
  },
  {
    name: "University of Ibadan",
    location: "Ibadan, Nigeria",
    programs: ["Internship Program", "Curriculum Development", "Tech Seminars"],
    status: "Active",
  },
  {
    name: "Ahmadu Bello University",
    location: "Zaria, Nigeria",
    programs: ["Research Collaboration", "Student Training", "Tech Innovation"],
    status: "Active",
  },
];

// School Partners
const schoolPartners = [
  {
    name: "Lagos Preparatory School",
    location: "Lagos, Nigeria",
    programs: [
      "Technology Implementation",
      "Digital Learning",
      "STEM Programs",
    ],
    status: "Active",
  },
  {
    name: "British International School",
    location: "Lagos, Nigeria",
    programs: ["EdTech Solutions", "Digital Classrooms", "Teacher Training"],
    status: "Active",
  },
];

const Partners = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    institution: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setSnackbar({
        open: true,
        message:
          "Thank you! We will contact you shortly about partnership opportunities.",
        severity: "success",
      });
      setFormData({
        name: "",
        email: "",
        institution: "",
        message: "",
      });
      setDialogOpen(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={styles.partnersPage}>
      {/* Hero Section */}
      <Box className={styles.heroSection}>
        {/* Animated Background Elements */}
        <Box className={styles.animatedBg}>
          <Box className={`${styles.particle} ${styles.particle1}`}></Box>
          <Box className={`${styles.particle} ${styles.particle2}`}></Box>
          <Box className={`${styles.particle} ${styles.particle3}`}></Box>
          <Box className={`${styles.particle} ${styles.particle4}`}></Box>
          <Box className={`${styles.particle} ${styles.particle5}`}></Box>
          <Box className={`${styles.particle} ${styles.particle6}`}></Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon1}`}>
            🤝
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon2}`}>
            🌍
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon3}`}>
            🎓
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Chip
              label="🤝 Our Partners"
              className={styles.heroChip}
              size="small"
            />
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Building <span className={styles.highlight}>Partnerships</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              Collaborating with schools and universities for technology
              innovation
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box className={styles.statsSection}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {statsData.map((stat, index) => (
              <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
                <Paper elevation={0} className={styles.statCard}>
                  <Box className={styles.statIcon}>{stat.icon}</Box>
                  <Typography variant="h3" className={styles.statNumber}>
                    {stat.number}
                  </Typography>
                  <Typography variant="body2" className={styles.statLabel}>
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Intro Section */}
      <Box className={styles.introSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.introContent}>
                <Chip
                  label="💡 Our Vision"
                  className={styles.sectionChip}
                  size="small"
                />
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  Passionate About{" "}
                  <span className={styles.highlight}>Technology Education</span>
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We are passionate about developing and implementing
                  technological products in schools that are{" "}
                  <strong>technology-oriented</strong>.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  Based on this, we are open to exploring productive technology
                  development collaboration opportunities with{" "}
                  <strong>schools and universities</strong>. We are currently
                  offering internship opportunities to a few universities in{" "}
                  <strong>Nigeria</strong>.
                </Typography>
                <Box className={styles.visionPoints}>
                  <Box className={styles.visionPoint}>
                    <CheckCircle className={styles.visionCheck} />
                    <Typography variant="body2">
                      Technology-oriented school products
                    </Typography>
                  </Box>
                  <Box className={styles.visionPoint}>
                    <CheckCircle className={styles.visionCheck} />
                    <Typography variant="body2">
                      University collaboration opportunities
                    </Typography>
                  </Box>
                  <Box className={styles.visionPoint}>
                    <CheckCircle className={styles.visionCheck} />
                    <Typography variant="body2">
                      Internship programs in Nigeria
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  className={styles.primaryBtn}
                  onClick={() => setDialogOpen(true)}
                  endIcon={<ArrowForward />}
                >
                  Become a Partner
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.introImage}>
                <Paper elevation={0} className={styles.imageCard}>
                  <Box className={styles.imagePlaceholder}>
                    <Typography variant="h1" className={styles.imageEmoji}>
                      🤝
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Partner Types Section */}
      <Box className={styles.typesSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Who We <span className={styles.highlight}>Partner With</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {partnerTypes.map((type) => (
              <Grid size={{ xs: 12, md: 4 }} key={type.id}>
                <Card className={styles.typeCard}>
                  <CardContent>
                    <Box
                      className={styles.typeIcon}
                      sx={{ background: `${type.color}15`, color: type.color }}
                    >
                      {type.icon}
                    </Box>
                    <Typography variant="h5" className={styles.typeTitle}>
                      {type.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.typeDescription}
                    >
                      {type.description}
                    </Typography>
                    <Box className={styles.typeFeatures}>
                      {type.features.map((feature, index) => (
                        <Box key={index} className={styles.typeFeature}>
                          <CheckCircle className={styles.typeFeatureCheck} />
                          <Typography variant="caption">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box className={styles.benefitsSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Benefits of{" "}
              <span className={styles.highlight}>Collaboration</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Paper elevation={0} className={styles.benefitCard}>
                  <Box className={styles.benefitIcon}>{benefit.icon}</Box>
                  <Typography variant="h6" className={styles.benefitTitle}>
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={styles.benefitDescription}
                  >
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* University Partners Section */}
      <Box className={styles.universitySection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              University <span className={styles.highlight}>Partners</span>
            </Typography>
            <Typography variant="body1" className={styles.sectionSubtitle}>
              Currently offering internship opportunities in Nigeria
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Nigerian Universities */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.partnerGroup}>
                <Typography variant="h6" className={styles.partnerGroupTitle}>
                  🇳🇬 Nigerian Universities
                </Typography>
                <Box className={styles.partnerList}>
                  {universityPartners.map((partner, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      className={styles.partnerItem}
                    >
                      <Box className={styles.partnerItemHeader}>
                        <Typography variant="h6" className={styles.partnerName}>
                          {partner.name}
                        </Typography>
                        <Chip
                          label={partner.status}
                          size="small"
                          className={styles.statusChip}
                        />
                      </Box>
                      <Box className={styles.partnerLocation}>
                        <LocationOn className={styles.locationIcon} />
                        <Typography variant="caption">
                          {partner.location}
                        </Typography>
                      </Box>
                      <Box className={styles.partnerPrograms}>
                        {partner.programs.map((program, idx) => (
                          <Chip
                            key={idx}
                            label={program}
                            size="small"
                            className={styles.programChip}
                          />
                        ))}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* International Schools */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.partnerGroup}>
                <Typography variant="h6" className={styles.partnerGroupTitle}>
                  🏫 School Partners
                </Typography>
                <Box className={styles.partnerList}>
                  {schoolPartners.map((partner, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      className={styles.partnerItem}
                    >
                      <Box className={styles.partnerItemHeader}>
                        <Typography variant="h6" className={styles.partnerName}>
                          {partner.name}
                        </Typography>
                        <Chip
                          label={partner.status}
                          size="small"
                          className={styles.statusChip}
                        />
                      </Box>
                      <Box className={styles.partnerLocation}>
                        <LocationOn className={styles.locationIcon} />
                        <Typography variant="caption">
                          {partner.location}
                        </Typography>
                      </Box>
                      <Box className={styles.partnerPrograms}>
                        {partner.programs.map((program, idx) => (
                          <Chip
                            key={idx}
                            label={program}
                            size="small"
                            className={styles.programChip}
                          />
                        ))}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Ready to <span className={styles.highlight}>Collaborate</span>?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              Join us in shaping the future of technology education. Partner
              with Wavelength Consultancy Solutions today.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                onClick={() => setDialogOpen(true)}
                startIcon={<Handshake />}
              >
                Become a Partner
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Partnership Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Box className={styles.dialogHeader}>
            <Typography variant="h5" className={styles.dialogTitleText}>
              🤝 Become a Partner
            </Typography>
            <IconButton
              onClick={() => setDialogOpen(false)}
              className={styles.dialogClose}
            >
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" className={styles.dialogSubtitle}>
            Fill in your details and we'll get in touch about partnership
            opportunities
          </Typography>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className={styles.formField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: "#8cc61d" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={styles.formField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#8cc61d" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Institution Name"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              required
              className={styles.formField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <School sx={{ color: "#8cc61d" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Your Message"
              name="message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange}
              required
              className={styles.formField}
              placeholder="Tell us about your institution and how you'd like to collaborate..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: "#8cc61d" }} />
                  </InputAdornment>
                ),
              }}
            />
            <DialogActions className={styles.dialogActions}>
              <Button
                variant="outlined"
                onClick={() => setDialogOpen(false)}
                className={styles.dialogCancelBtn}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                className={styles.dialogSubmitBtn}
                disabled={loading}
                endIcon={!loading && <Send />}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Submit Request"
                )}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Partners;
